import { match } from 'ts-pattern';
import { z } from 'zod';
import { payoutModalitiesSchema } from './PayoutModalities';
import { creativeSchema } from './Creative';
import type { Localization } from './Localization';
import type { BadgeProps } from '#ui/types';
import type { components } from '~~/types/Api';

export type Programme = components['schemas']['ProgrammeWithMerchant'];

export type PublicProgramme = components['schemas']['PublicProgrammeWithMerchant'];

export type BaseProgramme = Pick<PublicProgramme, 'id' | 'description' | 'merchant' | 'merchantId' | 'name' | 'payout' | 'merchantSlug'>;

export type ProgrammeApplicationStatus = components['schemas']['ProgrammeApplicationStatus'];

export type ProgrammeStatus = components['schemas']['ProgrammeStatus'];

export type ProgrammeApplicationSlim = components['schemas']['ProgrammeApplicationSlim'];

export type ProgrammeApplicationState = components['schemas']['ProgrammeApplicationState'];

// Derived access status for admin view — covers both legacy (hasAccess only) and v2 (application-based)
export type ProgrammeAccessStatus = 'HasAccess' | 'Pending' | 'OnHold' | 'Rejected' | 'NoAccess';

export const getProgrammeAccessStatus = (programme: ProgrammeStatus): ProgrammeAccessStatus => {
	if (programme.hasAccess) return 'HasAccess';
	const state = programme.application?.state;
	if (state === 'Open') return 'Pending';
	if (state === 'OnHold') return 'OnHold';
	if (state === 'Rejected') return 'Rejected';
	return 'NoAccess';
};

export const getProgrammeAccessStatusBadgeOptions = (status: ProgrammeAccessStatus): BadgeProps =>
	match(status)
		.returnType<BadgeProps>()
		.with('HasAccess', () => ({ color: 'success', variant: 'subtle' }))
		.with('Pending', () => ({ color: 'warning', variant: 'subtle' }))
		.with('OnHold', () => ({ color: 'warning', variant: 'outline' }))
		.with('Rejected', () => ({ color: 'error', variant: 'subtle' }))
		.with('NoAccess', () => ({ color: 'neutral', variant: 'subtle' }))
		.exhaustive();

export const getProgrammeAccessStatusName = (status: ProgrammeAccessStatus): Localization =>
	match(status)
		.returnType<Localization>()
		.with('HasAccess', () => (t) => t('programmeAccessStatus.hasAccess'))
		.with('Pending', () => (t) => t('programmeAccessStatus.pending'))
		.with('OnHold', () => (t) => t('programmeAccessStatus.onHold'))
		.with('Rejected', () => (t) => t('programmeAccessStatus.rejected'))
		.with('NoAccess', () => (t) => t('programmeAccessStatus.noAccess'))
		.exhaustive();

export const programmeSchema: z.ZodType<Programme> = z.object({
	merchant: z.string(),
	merchantId: z.string(),
	merchantSlug: z.string(),
	id: z.number(),
	name: z.string(),
	description: z.string(),
	isDefaultProgramme: z.boolean(),
	payout: payoutModalitiesSchema,
	creatives: z.array(creativeSchema),
	defaultLink: z.string().nullable().optional(),
});

export const baseProgrammeSchema: z.ZodType<BaseProgramme> = z.object({
	merchant: z.string(),
	merchantId: z.string(),
	merchantSlug: z.string(),
	id: z.number(),
	name: z.string(),
	description: z.string(),
	payout: payoutModalitiesSchema,
});

export const getProgrammeApplicationStatusBadgeOptions = (status: ProgrammeApplicationStatus) => match(status)
	.returnType<BadgeProps>()
	.with('Pending', () => ({ color: 'warning', variant: 'subtle' }))
	.with('Approved', () => ({ color: 'success', variant: 'subtle' }))
	.with('Unapproved', () => ({ color: 'neutral', variant: 'subtle' }))
	.with('Rejected', () => ({ color: 'error', variant: 'subtle' }))
	.with('Unknown', () => ({ color: 'error', variant: 'subtle' }))
	.exhaustive();

export const getProgrammeApplicationStatusName = (status: ProgrammeApplicationStatus) => match(status)
	.returnType<Localization>()
	.with('Pending', () => (t) => t('state.pending'))
	.with('Approved', () => (t) => t('state.approved'))
	.with('Unapproved', () => (t) => t('state.unapproved'))
	.with('Rejected', () => (t) => t('state.rejected'))
	.with('Unknown', () => (t) => t('state.unknown'))
	.exhaustive();

export const getProgrammeApplicationStatus = (
	programme: PublicProgramme,
): ProgrammeApplicationStatus => match(programme.applicationState)
	.returnType<ProgrammeApplicationStatus>()
	.with('Legacy', 'Unknown', () => programme.applicationStatus)
	.with('OnHold', 'Open', () => 'Pending')
	.with('Accepted', () => 'Approved')
	.with('Rejected', () => 'Rejected')
	.exhaustive();

export const canApplyToProgramme = (programme: PublicProgramme): boolean => {
	const status = getProgrammeApplicationStatus(programme);

	return match(status)
		.with('Approved', () => false)
		.with('Unapproved', () => true)
		.with('Pending', () => false)
		.with('Rejected', () => false)
		.with('Unknown', () => false)
		.exhaustive();
};
